import React, { useState, useMemo } from "react";
import {
  Calculator,
  Lock,
  Cloud,
  Moon,
  Sun,
  HelpCircle,
  X,
  Edit3,
  Globe,
} from "lucide-react";

// ==========================================
// Utility Functions
// ==========================================

const isValidIpInput = (val: string) => {
  if (val === "") return true;
  if (/[^0-9.]/.test(val)) return false;
  const parts = val.split(".");
  if (parts.length > 4) return false;
  return !parts.some((p) => p !== "" && (p.length > 3 || parseInt(p) > 255));
};

const expandIPv6 = (ipStr: string) => {
  let str = ipStr.trim();
  if (!str) return null;
  // Handle edge cases for double colons
  if (str === "::") str = "0000:0000:0000:0000:0000:0000:0000:0000";
  if (str.startsWith("::")) str = "0000" + str;
  if (str.endsWith("::")) str = str + "0000";

  if (str.includes("::")) {
    const parts = str.split("::");
    if (parts.length > 2) return null; // Invalid if multiple ::
    const left = parts[0] ? parts[0].split(":") : [];
    const right = parts[1] ? parts[1].split(":") : [];
    const missing = 8 - (left.length + right.length);
    if (missing < 0) return null;
    const zeroes = Array(missing).fill("0000");
    str = [...left, ...zeroes, ...right].join(":");
  }
  const blocks = str.split(":");
  if (blocks.length !== 8) return null;
  // Check hex validity
  if (blocks.some((b) => !/^[0-9a-fA-F]{1,4}$/.test(b))) return null;

  return blocks.map((b) => b.padStart(4, "0").toLowerCase());
};

// ==========================================
// Main App Component
// ==========================================

export default function App() {
  // Global State
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";
  const [mode, setMode] = useState("ipv4"); // "ipv4" or "ipv6"
  const [showFaq, setShowFaq] = useState(false);

  // IPv4 Calculator State
  const [ip, setIp] = useState("192.168.1.1");
  const [cidr, setCidr] = useState(24);
  const [inputHosts, setInputHosts] = useState("254");
  const [isEditingHosts, setIsEditingHosts] = useState(false);

  // IPv6 Calculator State
  const [ip6, setIp6] = useState("2001:db8::1");
  const [cidr6, setCidr6] = useState(64);

  // Styles Map
  const styles = useMemo(
    () => ({
      bg: isDark ? "bg-slate-900" : "bg-slate-50",
      text: isDark ? "text-slate-100" : "text-slate-900",
      textSubtle: isDark ? "text-slate-400" : "text-slate-600",
      panelBg: isDark ? "bg-slate-950" : "bg-white",
      panelBorder: isDark ? "border-slate-800" : "border-slate-200",
      inputBg: isDark ? "bg-slate-950" : "bg-white",
      inputBorder: isDark ? "border-slate-800" : "border-slate-300",
      label: isDark ? "text-slate-500" : "text-slate-700",
    }),
    [isDark]
  );

  // ==========================================
  // IPv4 Logic
  // ==========================================
  const result4 = useMemo(() => {
    if (!ip.match(/^(\d{1,3}\.){3}\d{1,3}$/)) return null;
    const parts = ip.split(".").map(Number);
    if (parts.some((p) => p < 0 || p > 255)) return null;

    const ipLong =
      (parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3];
    const maskLong = cidr === 0 ? 0 : ~0 << (32 - cidr);
    const netLong = ipLong & maskLong;
    const broadLong = netLong | ~maskLong;
    const longToIp = (l: number) =>
      [(l >>> 24) & 255, (l >>> 16) & 255, (l >>> 8) & 255, l & 255].join(".");

    const binaryMask =
      (maskLong >>> 0)
        .toString(2)
        .padStart(32, "0")
        .match(/.{1,8}/g)
        ?.join(".") || "";

    const firstByte = parts[0];
    const secondByte = parts[1];

    let rangeType = "Public Internet";
    let isPrivate = false;
    let isReserved = false;
    let reservedReason = "";

    if (firstByte === 10) {
      rangeType = "Private Network";
      isPrivate = true;
    } else if (firstByte === 172 && secondByte >= 16 && secondByte <= 31) {
      rangeType = "Private Network";
      isPrivate = true;
    } else if (firstByte === 192 && secondByte === 168) {
      rangeType = "Private Network";
      isPrivate = true;
    } else if (firstByte === 127) {
      rangeType = "Loopback";
      isPrivate = true;
      isReserved = true;
      reservedReason = "Reserved for loopback testing to the local host.";
    } else if (firstByte === 169 && secondByte === 254) {
      rangeType = "Link-Local (APIPA)";
      isPrivate = true;
      isReserved = true;
      reservedReason =
        "Reserved for Automatic Private IP Addressing (APIPA). Cannot be routed.";
    } else if (firstByte >= 224 && firstByte <= 239) {
      rangeType = "Multicast";
      isPrivate = false;
      isReserved = true;
      reservedReason = "Reserved for Multicast operations.";
    } else if (firstByte >= 240) {
      rangeType = "Reserved";
      isPrivate = false;
      isReserved = true;
      reservedReason = "Reserved for future use by the IETF.";
    }

    return {
      network: longToIp(netLong),
      broadcast: longToIp(broadLong),
      netmask: longToIp(maskLong),
      firstUsable: cidr >= 31 ? "N/A" : longToIp(netLong + 1),
      lastUsable: cidr >= 31 ? "N/A" : longToIp(broadLong - 1),
      hosts: Math.pow(2, 32 - cidr) - 2 > 0 ? Math.pow(2, 32 - cidr) - 2 : 0,
      binaryMask,
      rangeType,
      isPrivate,
      isReserved,
      reservedReason,
    };
  }, [ip, cidr]);

  const handleCidrChange = (e: any) => {
    const newCidr = parseInt(e.target.value, 10);
    setCidr(newCidr);
    const exactHosts = Math.pow(2, 32 - newCidr) - 2;
    setInputHosts(exactHosts > 0 ? exactHosts.toString() : "0");
  };

  const handleHostChange = (e: any) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setInputHosts(val);
    if (val !== "") {
      const req = parseInt(val, 10);
      let newCidr;
      if (req === 0) newCidr = 32;
      else {
        let h = Math.ceil(Math.log2(req + 2));
        if (h > 32) h = 32;
        newCidr = 32 - h;
      }
      setCidr(newCidr);
    }
  };

  // ==========================================
  // IPv6 Logic
  // ==========================================
  const result6 = useMemo(() => {
    const blocks = expandIPv6(ip6);
    if (!blocks) return null;

    const netBlocks = [];
    const lastBlocks = [];

    for (let i = 0; i < 8; i++) {
      const blockInt = parseInt(blocks[i], 16);
      const blockStartBit = i * 16;
      let maskInt = 0;

      if (cidr6 >= blockStartBit + 16) maskInt = 0xffff;
      else if (cidr6 > blockStartBit) {
        const bits = cidr6 - blockStartBit;
        maskInt = (0xffff << (16 - bits)) & 0xffff;
      } else {
        maskInt = 0x0000;
      }

      const netInt = blockInt & maskInt;
      const bcastInt = netInt | (~maskInt & 0xffff);

      netBlocks.push(netInt.toString(16).padStart(4, "0"));
      lastBlocks.push(bcastInt.toString(16).padStart(4, "0"));
    }

    const network = netBlocks.join(":");
    const last = lastBlocks.join(":");

    // Calculate total addresses using BigInt for 128-bit math
    const hostsBig = BigInt(1) << BigInt(128 - cidr6);
    let hostsStr = hostsBig.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Range Type Classification
    let rangeType = "Global Unicast";
    let isReserved = false;
    let reservedReason = "";

    const firstBlock = parseInt(blocks[0], 16);

    if (
      network === "0000:0000:0000:0000:0000:0000:0000:0001" &&
      cidr6 === 128
    ) {
      rangeType = "Loopback";
      isReserved = true;
      reservedReason =
        "Reserved for loopback testing (equivalent to IPv4 127.0.0.1).";
    } else if (firstBlock >= 0xfe80 && firstBlock <= 0xfebf) {
      rangeType = "Link-Local";
      isReserved = true;
      reservedReason =
        "Reserved for Link-Local routing. Not routable across the internet.";
    } else if (firstBlock >= 0xfc00 && firstBlock <= 0xfdff) {
      rangeType = "Unique Local";
      // Unique local is conceptually like private IPv4, not necessarily 'reserved' in the dangerous sense,
      // but we'll flag it for consistency if the user expects it. Let's not flag ULA as an error.
    } else if (firstBlock >= 0xff00) {
      rangeType = "Multicast";
      isReserved = true;
      reservedReason = "Reserved for Multicast operations.";
    } else if (network === "0000:0000:0000:0000:0000:0000:0000:0000") {
      rangeType = cidr6 === 128 ? "Unspecified" : "Default Route";
      if (cidr6 === 128) {
        isReserved = true;
        reservedReason =
          "Unspecified address, cannot be assigned to an interface.";
      }
    }

    return {
      network,
      last,
      hosts: hostsStr,
      rangeType,
      isPrivate:
        rangeType === "Unique Local" ||
        rangeType === "Link-Local" ||
        rangeType === "Loopback",
      isReserved,
      reservedReason,
    };
  }, [ip6, cidr6]);

  return (
    <div
      className={`min-h-screen w-full flex flex-col items-center justify-center p-4 ${styles.bg} ${styles.text} relative transition-colors duration-300`}
    >
      <header className="mb-6 text-center z-10">
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl mb-2">
          <span className="text-indigo-500">Free</span> Subnet Calculator
        </h1>
        <p className={`text-sm sm:text-base ${styles.textSubtle} max-w-lg mx-auto`}>
          A free, fast, and easy-to-use IPv4 and IPv6 subnet calculator for network administrators and engineers.
        </p>
      </header>

      {/* Theme Toggle Button */}
      <div className="absolute top-6 right-6 z-50">
        <button
          onClick={() => setTheme(isDark ? "light" : "dark")}
          className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border transition-all hover:border-blue-500 hover:shadow-blue-500/30 ${
            isDark
              ? "bg-slate-800 border-slate-600"
              : "bg-white border-slate-300"
          }`}
        >
          {isDark ? (
            <Moon size={16} className="text-blue-400" />
          ) : (
            <Sun size={16} className="text-orange-500" />
          )}
          <span className="text-[10px] font-bold">THEME</span>
        </button>
      </div>

      {/* Mode Toggle Switch */}
      <div
        className={`flex p-1 rounded-lg mb-8 shadow-inner w-56 relative z-10 transition-colors duration-300 ${
          isDark
            ? "bg-slate-950 border border-slate-800"
            : "bg-slate-200 border border-slate-300"
        }`}
      >
        <button
          onClick={() => setMode("ipv4")}
          className={`flex-1 py-1.5 text-sm font-bold rounded-md transition-all duration-300 ${
            mode === "ipv4"
              ? "bg-indigo-600 text-white shadow-md"
              : styles.textSubtle
          }`}
        >
          IPv4
        </button>
        <button
          onClick={() => setMode("ipv6")}
          className={`flex-1 py-1.5 text-sm font-bold rounded-md transition-all duration-300 ${
            mode === "ipv6"
              ? "bg-emerald-600 text-white shadow-md"
              : styles.textSubtle
          }`}
        >
          IPv6
        </button>
      </div>

      {/* 3D Flip Container */}
      <div
        style={{ perspective: "1200px" }}
        className="w-full max-w-[28rem] relative z-10"
      >
        {/* We use a grid to overlap both faces so the parent container naturally sizes to the tallest face.
            Using grid directly on the preserve-3d element prevents CSS from flattening the 3D context. */}
        <div
          className="w-full relative transition-transform duration-700 ease-in-out grid"
          style={{
            transformStyle: "preserve-3d",
            transform: mode === "ipv6" ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          {/* ======================================= */}
          {/* FRONT FACE: IPv4 Calculator             */}
          {/* ======================================= */}
          <div
            className={`col-start-1 row-start-1 w-full ${styles.panelBg} border border-indigo-500/50 rounded-xl shadow-2xl overflow-hidden`}
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
            }}
          >
            <div className="bg-indigo-600 px-4 py-3 flex justify-between items-center text-white font-bold text-sm">
              <span className="flex items-center gap-2">
                <Calculator size={16} /> IPv4 Calculator
              </span>
              <button
                onClick={() => setShowFaq(true)}
                className="hover:text-indigo-200 transition-colors p-1"
                title="Help / FAQ"
              >
                <HelpCircle size={16} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div>
                <label
                  className={`text-[10px] font-bold ${styles.label} uppercase tracking-wider`}
                >
                  IP Address
                </label>
                <input
                  inputMode="decimal"
                  type="text"
                  value={ip}
                  onChange={(e) => {
                    if (isValidIpInput(e.target.value)) setIp(e.target.value);
                  }}
                  className={`w-full mt-1 ${styles.inputBg} border-2 border-blue-500/50 focus:border-blue-500 rounded px-3 py-2 text-sm font-mono outline-none shadow-sm transition-all text-inherit`}
                />
              </div>
              <div>
                <label
                  className={`text-[10px] font-bold ${styles.label} uppercase tracking-wider flex justify-between`}
                >
                  <span>Subnet Mask Prefix</span>
                  <span className="text-indigo-500 text-xs">/{cidr}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="32"
                  value={cidr}
                  onChange={handleCidrChange}
                  className="w-full mt-2 accent-indigo-500 cursor-pointer"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div
                  className={`${styles.inputBg} p-2 rounded border ${styles.inputBorder} col-span-2 flex items-center justify-between`}
                >
                  <span
                    className={`text-[9px] ${styles.label} font-bold uppercase`}
                  >
                    Range Type
                  </span>
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1 cursor-help
                          ${
                            result4?.isReserved
                              ? "text-red-500 bg-red-500/10"
                              : result4?.isPrivate
                              ? "text-orange-500 bg-orange-500/10"
                              : "text-emerald-500 bg-emerald-500/10"
                          }`}
                    title={result4?.isReserved ? result4.reservedReason : ""}
                  >
                    {result4?.isReserved ? (
                      <span className="mr-1">⚠️</span>
                    ) : result4?.isPrivate ? (
                      <Lock size={10} />
                    ) : (
                      <Cloud size={10} />
                    )}
                    {result4?.rangeType || "Public Internet"}
                  </span>
                </div>
                {[
                  {
                    l: "Network",
                    v: result4?.network,
                    c: isDark ? "text-emerald-400" : "text-emerald-600",
                  },
                  {
                    l: "Broadcast",
                    v: result4?.broadcast,
                    c: isDark ? "text-orange-400" : "text-orange-600",
                  },
                  {
                    l: "First IP",
                    v: result4?.firstUsable,
                    c: isDark ? "text-blue-400" : "text-blue-600",
                  },
                  {
                    l: "Last IP",
                    v: result4?.lastUsable,
                    c: isDark ? "text-blue-400" : "text-blue-600",
                  },
                ].map((f, i) => (
                  <div
                    key={i}
                    className={`${styles.inputBg} p-2 rounded border ${styles.inputBorder}`}
                  >
                    <div
                      className={`text-[9px] ${styles.label} font-bold uppercase`}
                    >
                      {f.l}
                    </div>
                    <div className={`text-xs font-mono truncate mt-0.5 ${f.c}`}>
                      {f.v || "---.---.---.---"}
                    </div>
                  </div>
                ))}
                <div
                  className={`${styles.inputBg} p-2 rounded border ${styles.inputBorder}`}
                >
                  <div
                    className={`text-[9px] ${styles.label} font-bold uppercase`}
                  >
                    Subnet Mask
                  </div>
                  <div
                    className={`text-xs font-mono truncate mt-0.5 ${
                      isDark ? "text-purple-400" : "text-purple-600"
                    }`}
                  >
                    {result4?.netmask || "---.---.---.---"}
                  </div>
                </div>
                <div
                  className={`${styles.inputBg} p-2 rounded border ${styles.inputBorder} focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all cursor-text`}
                  onClick={() =>
                    document.getElementById("hosts-input")?.focus()
                  }
                >
                  <div
                    className={`text-[9px] ${styles.label} font-bold uppercase flex justify-between items-center`}
                  >
                    <span>Hosts</span>
                    <Edit3 size={10} className="opacity-50 text-indigo-500" />
                  </div>
                  <input
                    id="hosts-input"
                    type="text"
                    inputMode="numeric"
                    value={
                      isEditingHosts
                        ? inputHosts
                        : Number(inputHosts || 0).toLocaleString()
                    }
                    onFocus={() => setIsEditingHosts(true)}
                    onBlur={() => {
                      setIsEditingHosts(false);
                      const actualHosts = Math.pow(2, 32 - cidr) - 2;
                      setInputHosts(
                        actualHosts > 0 ? actualHosts.toString() : "0"
                      );
                    }}
                    onChange={handleHostChange}
                    className={`w-full bg-transparent text-sm font-bold mt-0.5 outline-none ${styles.text}`}
                  />
                </div>
                <div
                  className={`${styles.inputBg} p-3 rounded border ${styles.inputBorder} col-span-2`}
                >
                  <div
                    className={`text-[9px] ${styles.label} font-bold uppercase`}
                  >
                    Binary Mask
                  </div>
                  <div
                    className={`text-[10px] sm:text-xs font-mono tracking-[0.15em] sm:tracking-[0.2em] mt-1 text-center ${
                      isDark ? "text-pink-500" : "text-pink-600"
                    }`}
                  >
                    {result4?.binaryMask ||
                      "--------.--------.--------.--------"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ======================================= */}
          {/* BACK FACE: IPv6 Calculator              */}
          {/* ======================================= */}
          <div
            className={`col-start-1 row-start-1 w-full ${styles.panelBg} border border-emerald-500/50 rounded-xl shadow-2xl overflow-hidden`}
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <div className="bg-emerald-600 px-4 py-3 flex justify-between items-center text-white font-bold text-sm">
              <span className="flex items-center gap-2">
                <Globe size={16} /> IPv6 Calculator
              </span>
              <button
                onClick={() => setShowFaq(true)}
                className="hover:text-emerald-200 transition-colors p-1"
                title="Help / FAQ"
              >
                <HelpCircle size={16} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div>
                <label
                  className={`text-[10px] font-bold ${styles.label} uppercase tracking-wider`}
                >
                  IPv6 Address
                </label>
                <input
                  type="text"
                  value={ip6}
                  onChange={(e) => setIp6(e.target.value)}
                  className={`w-full mt-1 ${styles.inputBg} border-2 border-emerald-500/50 focus:border-emerald-500 rounded px-3 py-2 text-sm font-mono outline-none shadow-sm transition-all text-inherit`}
                  placeholder="e.g. 2001:db8::1"
                />
              </div>
              <div>
                <label
                  className={`text-[10px] font-bold ${styles.label} uppercase tracking-wider flex justify-between`}
                >
                  <span>Prefix Length (CIDR)</span>
                  <span className="text-emerald-500 text-xs">/{cidr6}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="128"
                  value={cidr6}
                  onChange={(e: any) => setCidr6(parseInt(e.target.value, 10))}
                  className="w-full mt-2 accent-emerald-500 cursor-pointer"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div
                  className={`${styles.inputBg} p-2 rounded border ${styles.inputBorder} col-span-2 flex items-center justify-between`}
                >
                  <span
                    className={`text-[9px] ${styles.label} font-bold uppercase`}
                  >
                    Range Type
                  </span>
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1 cursor-help
                          ${
                            result6?.isReserved
                              ? "text-red-500 bg-red-500/10"
                              : result6?.isPrivate
                              ? "text-orange-500 bg-orange-500/10"
                              : "text-emerald-500 bg-emerald-500/10"
                          }`}
                    title={result6?.isReserved ? result6.reservedReason : ""}
                  >
                    {result6?.isReserved ? (
                      <span className="mr-1">⚠️</span>
                    ) : result6?.isPrivate ? (
                      <Lock size={10} />
                    ) : (
                      <Cloud size={10} />
                    )}
                    {result6?.rangeType || "Invalid"}
                  </span>
                </div>

                <div
                  className={`${styles.inputBg} p-2 rounded border ${styles.inputBorder} col-span-2`}
                >
                  <div
                    className={`text-[9px] ${styles.label} font-bold uppercase`}
                  >
                    Network Address
                  </div>
                  <div
                    className={`text-[10px] sm:text-xs font-mono truncate mt-0.5 ${
                      isDark ? "text-emerald-400" : "text-emerald-600"
                    }`}
                  >
                    {result6?.network ||
                      "----:----:----:----:----:----:----:----"}
                  </div>
                </div>

                <div
                  className={`${styles.inputBg} p-2 rounded border ${styles.inputBorder} col-span-2`}
                >
                  <div
                    className={`text-[9px] ${styles.label} font-bold uppercase`}
                  >
                    Last Address
                  </div>
                  <div
                    className={`text-[10px] sm:text-xs font-mono truncate mt-0.5 ${
                      isDark ? "text-blue-400" : "text-blue-600"
                    }`}
                  >
                    {result6?.last || "----:----:----:----:----:----:----:----"}
                  </div>
                </div>

                <div
                  className={`${styles.inputBg} p-3 rounded border ${styles.inputBorder} col-span-2`}
                >
                  <div
                    className={`text-[9px] ${styles.label} font-bold uppercase`}
                  >
                    Total Addresses
                  </div>
                  <div
                    className={`text-sm font-bold mt-1 break-all ${
                      isDark ? "text-purple-400" : "text-purple-600"
                    }`}
                  >
                    {result6?.hosts || "0"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Modal Overlay */}
      {showFaq && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in"
          onClick={() => setShowFaq(false)}
        >
          <div
            className={`w-full max-w-md ${styles.panelBg} border ${styles.panelBorder} rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-slate-500/20">
              <h2
                className={`text-lg font-bold flex items-center gap-2 ${
                  mode === "ipv4" ? "text-indigo-500" : "text-emerald-500"
                }`}
              >
                <HelpCircle size={20} /> About the Subnet Calculator
              </h2>
              <button
                onClick={() => setShowFaq(false)}
                className={`p-1 rounded-full hover:bg-slate-500/10 transition-colors ${styles.textSubtle}`}
              >
                <X size={20} />
              </button>
            </div>
            <div
              className={`p-6 space-y-4 text-sm ${styles.textSubtle} leading-relaxed max-h-[70vh] overflow-y-auto`}
            >
              <p>
                A subnet calculator is a tool used by network administrators to
                divide an IP network into logically distinct, smaller networks
                (subnets).
              </p>
              <h3 className={`font-bold mt-4 mb-2 ${styles.text}`}>
                IPv4 Mode
              </h3>
              <ul className="list-disc pl-5 space-y-2 text-inherit">
                <li>
                  <strong className={styles.text}>Network & Broadcast:</strong>{" "}
                  The unusable boundaries of your subnet.
                </li>
                <li>
                  <strong className={styles.text}>Usable IP Range:</strong> The
                  first and last IP addresses you can assign to devices.
                </li>
                <li>
                  <strong className={styles.text}>Hosts:</strong> The maximum
                  number of devices the subnet can support. You can type a
                  number here to automatically find the right CIDR prefix!
                </li>
              </ul>
              <h3 className={`font-bold mt-4 mb-2 ${styles.text}`}>
                IPv6 Mode
              </h3>
              <p>
                IPv6 uses 128-bit addresses, creating a virtually limitless
                supply of IPs. Because it operates differently than IPv4, there
                is no "Broadcast" address, and "Hosts" refers to the total
                number of addresses available in the prefix block.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
